/**
 * save-controller.ts — Phase 051 Plan 01 Task 1.
 *
 * Pure async pipeline. No React, no Zustand. The React-side seam
 * (`use-save-pipeline.ts`) injects the 4 store-backed effects + the
 * mutation client's `addBlock` as plain function parameters.
 *
 * Per-block atomic on success (D-051-03 + AP10):
 *   1. addBlock(projected envelope)  — whole-block re-emit (AP7 + OQ#1)
 *   2. clearBlockDraft(blockId)      — only after addBlock resolved
 *   3. commitBlockBaseline(blockId, env.properties) — patch in-memory baseline
 *
 * Stop-on-first failure (D-051-03):
 *   - Committed blocks before the failure stay committed (drafts cleared +
 *     baseline patched).
 *   - The failing block + every remaining-unattempted block stays dirty.
 *   - On SHACL → `showShaclErrorToast(err)`; on any other Error →
 *     `showGenericErrorToast(err)`.
 *
 * Whole-block-replace contract (Codex review §Caveat 2026-05-23 — MEDIUM
 * downgrade): the `add_block` handler's upsert semantics in cogo-platform
 * are not directly verified in this worktree. The defensive contract here
 * passes the projected `__aboxEnvelope` VERBATIM, never a partial draft
 * delta — a partial payload would silently delete properties under a
 * replace-style handler. Test 4 asserts the wire payload carries
 * baseline + draft keys, not just deltas.
 */

import type { LoadedApp, BlockDef } from '@/features/page-editor/store/types'
import { applyDrafts } from '@/features/preview/projector/apply-drafts'
import {
  readEnvelope,
  type AboxEnvelope,
} from '@/features/design-system/ontology/abox-render-prep'
import {
  isShaclViolationError,
  type ShaclViolationError,
} from '@/features/design-system/ontology/shacl-error'
import type {
  AddBlockArgs,
  AddBlockResult,
} from '@/features/design-system/ontology/mcp-mutation-client'

export interface SavePipelineDeps {
  tenantId: string
  appId: string
  currentProject: LoadedApp
  drafts: Record<string, Record<string, unknown>>
  dirtyBlockIds: ReadonlySet<string>
  addBlock: (args: AddBlockArgs) => Promise<AddBlockResult>
  clearBlockDraft: (blockId: string) => void
  commitBlockBaseline: (
    blockId: string,
    properties: Record<string, unknown>,
  ) => void
  /**
   * Phase 051.1 D-051.1-04 (Codex P2 fix) — Read the current per-block
   * draft snapshot. Called BEFORE `addBlock` (snapshot) and AGAIN AFTER
   * the await resolves (post-await read). When the two snapshots
   * deep-equal, the controller clears the draft (normal cleanup path);
   * otherwise a mid-flight edit was detected and the draft is preserved.
   */
  readBlockDraft: (blockId: string) => Record<string, unknown> | undefined
  showShaclErrorToast: (err: ShaclViolationError) => void
  showGenericErrorToast: (err: Error) => void
  showSuccessToast: (committedCount: number) => void
}

/**
 * Phase 051.1 D-051.1-06 — Deep-equality helper for draft snapshot match.
 *
 * The Save controller compares two snapshots of the same blockId's draft
 * (pre-await + post-await). Both sides are `Record<string, unknown>`
 * shapes the property panel writes — pure JSON (no functions, no Symbols,
 * no Dates, no circular refs — verified at draft-store.ts:136-156 which
 * uses the same JSON.stringify round-trip for its baseline diff). Same
 * trade-off (O(n) string scan, correct under the pure-JSON constraint)
 * carries over.
 *
 * Inline helper instead of exporting `deepEqualJson` from draft-store.ts
 * to keep the controller's dependency surface to the SavePipelineDeps
 * interface only (no cross-module import of a private helper). Lodash is
 * NOT a project dependency (verified via `grep lodash package.json`); a
 * dedicated deep-equal helper does not exist outside draft-store.
 */
function draftEquals(
  a: Record<string, unknown> | undefined,
  b: Record<string, unknown> | undefined,
): boolean {
  if (a === b) return true
  if (a == null || b == null) return a === b
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    // JSON.stringify can throw on circular refs. The property panel only
    // writes plain-JSON values; this branch is structurally unreachable
    // but defensive against future shape drift.
    return false
  }
}

export interface SavePipelineResult {
  committedCount: number
  failedAt?: { blockId: string; error: Error }
}

export async function runSavePipeline(
  deps: SavePipelineDeps,
): Promise<SavePipelineResult> {
  // AP1 + OQ#4 — MANDATORY first executable line. Read merged envelopes off
  // the projected LoadedApp, NEVER off deps.currentProject (which is the
  // BASELINE; silently saving baseline is Pitfall 1 / AP1).
  const projected = applyDrafts(deps.currentProject, deps.drafts)

  // Codex 2026-05-23 review fix #3 — one-pass Map<env.id, envelope> BEFORE
  // the dirty-blocks loop. Replaces the prior O(N²) inner Object.values
  // scan (one nested walk per dirty block). Total cost is O(N) where N is
  // the number of projected ABox blocks. readEnvelope returns null for
  // non-ABox blocks; those are silently excluded from the index.
  const envelopeById = new Map<string, AboxEnvelope>()
  for (const block of Object.values(projected.blocks)) {
    const env = readEnvelope(block as BlockDef)
    if (env?.id) envelopeById.set(env.id, env)
  }

  let committedCount = 0
  for (const blockId of deps.dirtyBlockIds) {
    const envelope = envelopeById.get(blockId)
    if (!envelope) {
      // AP10 defensive — dirtyBlockId not in projected blocks (e.g. race
      // between draft creation and block deletion, OR a non-ABox block whose
      // readEnvelope returned null at index time). Skip and continue; do
      // NOT throw.
      continue
    }

    // Phase 051.1 D-051.1-04 — Codex P2 fix. Snapshot the block's draft
    // BEFORE the addBlock await. After the await resolves, re-read the
    // draft. If the operator typed in the property panel during the
    // network round-trip, the post-await read will differ — clearing
    // the draft would silently drop their mid-flight edit. Only clear
    // when the snapshots deep-equal (no mid-flight edit detected).
    // Otherwise commit the baseline (the SAVED state did land server-
    // side) but preserve the draft (the newer in-flight delta survives
    // for the operator's next re-save; block stays in dirtyBlockIds().)
    const draftSnapshot = deps.readBlockDraft(blockId)
    try {
      // AP7 + OQ#1 — projected __aboxEnvelope IS the wire-ready
      // block_definition (whole-block replace semantics — defensive
      // contract; handler upsert verification deferred per RESEARCH
      // §Caveat MEDIUM downgrade 2026-05-23). NEVER assemble from draft
      // delta — partial payloads would silently delete properties under
      // a replace-style handler.
      await deps.addBlock({
        tenant_id: deps.tenantId,
        app_id: deps.appId,
        block_definition: envelope as unknown as Record<string, unknown>,
      })
      // Phase 051.1 D-051.1-04 — post-await re-read + conditional clear.
      const currentDraft = deps.readBlockDraft(blockId)
      const draftUnchanged = draftEquals(currentDraft, draftSnapshot)
      // AP10 — per-block atomic on success. Cleanup runs INSIDE the
      // per-block try, AFTER addBlock resolves. NEVER post-loop. The
      // clearBlockDraft is now conditional on the snapshot match; the
      // commitBlockBaseline is UNCONDITIONAL — the SAVED snapshot did
      // land server-side regardless of mid-flight edits.
      if (draftUnchanged) {
        deps.clearBlockDraft(blockId)
      }
      // Else: mid-flight edit detected. Preserve the draft. The block
      // stays in dirtyBlockIds(); DirtyCountChip stays elevated; the
      // operator re-clicks Save when they're done editing.
      deps.commitBlockBaseline(blockId, envelope.properties)
      committedCount += 1
    } catch (err) {
      // D-051-03 — stop-on-first failure. Failing block + remaining blocks
      // stay dirty (drafts NOT cleared); committed blocks before failure
      // DO have drafts cleared + baseline patched.
      if (isShaclViolationError(err)) {
        deps.showShaclErrorToast(err)
      } else {
        deps.showGenericErrorToast(err as Error)
      }
      return {
        committedCount,
        failedAt: { blockId, error: err as Error },
      }
    }
  }

  // D-051-05 — summary toast ONLY on full-pipeline success (NEVER on partial
  // commit — DirtyCountChip drop is the partial-progress signal).
  if (committedCount > 0) {
    deps.showSuccessToast(committedCount)
  }
  return { committedCount }
}
